import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import LinkIcon from "@mui/icons-material/Link";
import SyncIcon from "@mui/icons-material/Sync";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { apiUrl } from "../api/endpoints";

const statusLabel = (status, linked) => {
  if (!linked) return "Not linked";
  if (status?.state === "syncing") return "Syncing";
  if (status?.state === "connected" && ((status?.summary?.errors?.length || 0) + (status?.summary?.conflicts?.length || 0) + Number(status?.summary?.drafts || 0) > 0)) return "Review rows";
  if (status?.state === "connected") return "Synced";
  if (status?.state === "error") return "Needs attention";
  return "Ready to sync";
};
const safeSheetName = (value) => String(value || "Assets").replace(/[\\/?*:[\]]/g, "-").trim().slice(0, 100) || "Assets";

const GoogleSheetsAssetSyncPanel = ({ value, subscribers, onChange, hasUnsavedChanges, onSave }) => {
  const [status, setStatus] = useState({ credentialsReady: false, subscribers: {} });
  const [search, setSearch] = useState("");
  const [syncingId, setSyncingId] = useState(null);
  const [notice, setNotice] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const response = await axios.get(apiUrl("configuration/google-sheets/assets/status"));
      setStatus(response.data?.data || { credentialsReady: false, subscribers: {} });
    } catch (error) {
      setNotice(error.response?.data?.message || "Could not load Google Sheets connection status.");
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const timer = window.setInterval(loadStatus, 10000);
    return () => window.clearInterval(timer);
  }, [loadStatus]);

  const linksBySubscriber = useMemo(
    () => new Map((value.subscribers || []).map((item) => [String(item.subscriberId), item])),
    [value.subscribers]
  );
  const visibleSubscribers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return subscribers;
    return subscribers.filter((subscriber) =>
      [subscriber.name, subscriber.email, subscriber.id].some((item) => String(item || "").toLowerCase().includes(term))
    );
  }, [search, subscribers]);
  const duplicateTargets = useMemo(() => {
    const counts = new Map();
    (value.subscribers || []).filter((item) => item.enabled !== false && item.spreadsheetId).forEach((item) => {
      const key = `${item.spreadsheetId.trim()}:${safeSheetName(item.sheetName).toLowerCase()}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([key]) => key));
  }, [value.subscribers]);

  const updateLink = (subscriberId, patch) => {
    const subscriber = subscribers.find((item) => Number(item.id) === Number(subscriberId));
    const current = linksBySubscriber.get(String(subscriberId)) || {
      subscriberId: Number(subscriberId), enabled: true, spreadsheetId: "",
      sheetName: safeSheetName(subscriber?.name || `Subscriber ${subscriberId}`),
    };
    const targetChanged = (Object.prototype.hasOwnProperty.call(patch, "spreadsheetId") && patch.spreadsheetId !== current.spreadsheetId) ||
      (Object.prototype.hasOwnProperty.call(patch, "sheetName") && patch.sheetName !== current.sheetName);
    const next = {
      ...current, ...patch, subscriberId: Number(subscriberId),
      ...(targetChanged ? { syncState: null, lastSyncedAt: null, lastAttemptAt: null, lastError: null, lastSummary: null, dataVersion: 0 } : {}),
    };
    onChange({
      ...value,
      subscribers: [...(value.subscribers || []).filter((item) => Number(item.subscriberId) !== Number(subscriberId)), next]
        .sort((a, b) => a.subscriberId - b.subscriberId),
    });
  };

  const syncNow = async (subscriberId) => {
    setSyncingId(subscriberId);
    setNotice("");
    try {
      if (hasUnsavedChanges) {
        const saved = await onSave?.();
        if (!saved) {
          setNotice("Save failed, so sync was not started.");
          return;
        }
      }
      const response = await axios.post(apiUrl("configuration/google-sheets/assets/sync"), { subscriberId });
      const summary = response.data?.data?.summary || {};
      const issueCount = (summary.errors?.length || 0) + (summary.conflicts?.length || 0) + Number(summary.drafts || 0);
      setNotice(issueCount
        ? `Sync completed with ${issueCount} row${issueCount === 1 ? "" : "s"} needing attention. Review Sync Status and Sync Message in the sheet.`
        : `Sync completed. ${summary.totalAssets || 0} asset${summary.totalAssets === 1 ? "" : "s"} are aligned.`);
      await loadStatus();
    } catch (error) {
      setNotice(error.response?.data?.message || error.response?.data?.error || "Google Sheets sync failed.");
    } finally {
      setSyncingId(null);
    }
  };

  const copyServiceAccount = async () => {
    if (!status.serviceAccountEmail) return;
    await navigator.clipboard.writeText(status.serviceAccountEmail);
    setNotice("Service account email copied.");
  };

  return (
    <>
      <section className="system-config-panel sheets-sync-hero">
        <div className="system-config-panel-head">
          <div>
            <h3>Google Sheets asset sync</h3>
            <p>Connect one spreadsheet per subscriber. Changes flow both ways without creating duplicate asset cards.</p>
          </div>
          <span className={`sheets-sync-credential ${status.credentialsReady ? "ready" : "missing"}`}>
            {status.credentialsReady ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
            {status.credentialsReady ? "Google credentials ready" : "Google credentials missing"}
          </span>
        </div>

        <div className="sheets-sync-setup-grid">
          <label className="system-config-switch-card">
            <span><strong>Automatic sync</strong><small>Continuously checks linked sheets for changes.</small></span>
            <input type="checkbox" checked={value.enabled !== false} onChange={(event) => onChange({ ...value, enabled: event.target.checked })} />
          </label>
          <label className="sheets-sync-interval">
            <span>
              <strong>Check every</strong>
              <small>Automatic polling interval.</small>
            </span>
            <span className="sheets-sync-interval-control">
              <input
                type="number"
                min="10"
                max="300"
                value={value.pollIntervalSeconds || 15}
                onChange={(event) => onChange({ ...value, pollIntervalSeconds: event.target.value })}
                aria-label="Google Sheets sync interval in seconds"
              />
              <em>sec</em>
            </span>
          </label>
        </div>

        <div className="sheets-sync-share-note">
          <LinkIcon />
          <span>
            <strong>Share every spreadsheet with the service account as Editor</strong>
            <small>{status.serviceAccountEmail || "Set GOOGLE_SERVICE_ACCOUNT_EMAIL and GOOGLE_PRIVATE_KEY on the API server."}</small>
          </span>
          {status.serviceAccountEmail ? <button type="button" onClick={copyServiceAccount} aria-label="Copy service account email"><ContentCopyIcon /></button> : null}
        </div>
      </section>

      <section className="system-config-panel">
        <div className="system-config-panel-head sheets-sync-list-head">
          <div><h3>Subscriber connections</h3><p>{value.subscribers?.filter((item) => item.spreadsheetId).length || 0} of {subscribers.length} subscribers linked</p></div>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search subscriber or email" />
        </div>

        <div className="sheets-sync-list">
          {visibleSubscribers.map((subscriber) => {
            const link = linksBySubscriber.get(String(subscriber.id)) || {};
            const subscriberStatus = status.subscribers?.[String(subscriber.id)];
            const linked = Boolean(link.spreadsheetId);
            const targetKey = `${String(link.spreadsheetId || "").trim()}:${safeSheetName(link.sheetName).toLowerCase()}`;
            const duplicateTarget = linked && duplicateTargets.has(targetKey);
            const rowIssueCount = (subscriberStatus?.summary?.errors?.length || 0) +
              (subscriberStatus?.summary?.conflicts?.length || 0) + Number(subscriberStatus?.summary?.drafts || 0);
            const badgeState = subscriberStatus?.state || (linked ? "ready" : "unlinked");
            return (
              <article className={`sheets-sync-card ${linked ? "linked" : ""}`} key={subscriber.id}>
                <div className="sheets-sync-card-title">
                  <span className="sheets-sync-avatar">{String(subscriber.name || subscriber.email || "S").slice(0, 1).toUpperCase()}</span>
                  <span><strong>{subscriber.name || `Subscriber ${subscriber.id}`}</strong><small>{subscriber.email || `ID ${subscriber.id}`}</small></span>
                  <em className={`sheets-sync-status ${duplicateTarget ? "error" : rowIssueCount ? "warning" : badgeState}`}>{duplicateTarget ? "Duplicate tab" : statusLabel(subscriberStatus, linked)}</em>
                </div>
                <div className="sheets-sync-fields">
                  <label className="wide"><span>Spreadsheet URL or ID</span><input value={link.spreadsheetId || ""} onChange={(event) => updateLink(subscriber.id, { spreadsheetId: event.target.value })} placeholder="https://docs.google.com/spreadsheets/d/..." /></label>
                  <label><span>Subscriber sheet tab</span><input value={link.sheetName || safeSheetName(subscriber.name)} onChange={(event) => updateLink(subscriber.id, { sheetName: safeSheetName(event.target.value) })} placeholder={safeSheetName(subscriber.name)} /></label>
                  <label className="sheets-sync-enable"><span>Connection</span><span className="sheets-sync-toggle-row"><input type="checkbox" checked={link.enabled !== false} onChange={(event) => updateLink(subscriber.id, { enabled: event.target.checked })} /> Enabled</span></label>
                </div>
                <div className="sheets-sync-card-footer">
                  <span>
                    {subscriberStatus?.lastSyncedAt ? `Last synced ${new Date(subscriberStatus.lastSyncedAt).toLocaleString()}` : "Not synced yet"}
                    {duplicateTarget ? <small>Choose a unique tab name for this subscriber.</small> : subscriberStatus?.lastError ? <small>{subscriberStatus.lastError}</small> : null}
                    {!duplicateTarget && !subscriberStatus?.lastError && rowIssueCount ? (
                      <small>{rowIssueCount} sheet row{rowIssueCount === 1 ? "" : "s"} need correction or submission.</small>
                    ) : null}
                  </span>
                  <button type="button" className="system-config-add-btn secondary" disabled={!linked || duplicateTarget || syncingId !== null || !status.credentialsReady} onClick={() => syncNow(subscriber.id)}>
                    <SyncIcon className={syncingId === subscriber.id ? "spin" : ""} /> {syncingId === subscriber.id ? "Syncing..." : hasUnsavedChanges ? "Save & sync" : "Sync now"}
                  </button>
                </div>
                {rowIssueCount ? (
                  <details className="sheets-sync-issues">
                    <summary>Review sheet issues</summary>
                    <ul>
                      {(subscriberStatus?.summary?.errors || []).slice(0, 4).map((item, index) => (
                        <li key={`error-${item.row || index}`}><strong>Row {item.row || "?"}:</strong> {item.message}</li>
                      ))}
                      {(subscriberStatus?.summary?.conflicts || []).slice(0, 4).map((item, index) => (
                        <li key={`conflict-${item.row || index}`}><strong>Row {item.row || "?"}:</strong> {item.message}</li>
                      ))}
                      {subscriberStatus?.summary?.drafts ? <li><strong>{subscriberStatus.summary.drafts}</strong> draft row(s) are waiting for Submit.</li> : null}
                    </ul>
                  </details>
                ) : null}
              </article>
            );
          })}
          {!visibleSubscribers.length ? <div className="system-config-type-empty">No subscribers match your search.</div> : null}
        </div>
        {notice ? <div className="sheets-sync-notice">{notice}</div> : null}
      </section>
    </>
  );
};

export default GoogleSheetsAssetSyncPanel;
